import React, { useContext, useMemo } from 'react';
import './CorporateActionsWidgetGrid.scss';
import { Grid, GridApi } from '@instinet-analytics/semantic-ui-components';
import { useWidgetGridState } from 'widgets/store/grid/useWidgetGridState';
import {
    corporateActionsColumnDefinitionsDictionary,
    getCorporateActionsColDefs,
    SymbolEventColumn,
} from 'widgets/corpActions/widget/grid/columnDefinitions';
import AppContext from 'app/AppContext';
import { SymbolEvent } from 'widgets/corpActions/data/SymbolEvent';
import { useFilteredCorporateActionsData } from 'widgets/corpActions/widget/hooks';
import DateTimeExpression from 'utils/filter/expressions/DateTimeExpression';
import { useWidgetEditedColumnNames } from 'grid/columnNames/hooks';
import { EnrichedSymbolEvent } from 'widgets/corpActions/store/data/earningsAftMktOnTMinus1';

export interface CorporateActionsWidgetGridProps {
    height: number;
    onGridApiSet: (gridApi: GridApi) => void;
    onRowSelected: (row: SymbolEvent | undefined) => void;
}

export function CorporateActionsWidgetGrid(
    props: CorporateActionsWidgetGridProps
) {
    const { height, onGridApiSet, onRowSelected } = props;
    const context = useContext(AppContext);

    const filteredData = useFilteredCorporateActionsData();

    const useWidgetEditedColumnNamesProps = useWidgetEditedColumnNames();

    const onGridReady = (api: GridApi) => {
        onGridApiSet(api);
    };

    const colDefs = useMemo(
        () => getCorporateActionsColDefs(context),
        [context]
    );

    const { gridPropsFromState, columnDefs } = useWidgetGridState<
        SymbolEvent,
        SymbolEventColumn
    >(corporateActionsColumnDefinitionsDictionary, colDefs);

    const style = { height: `${height}px` };

    function onSelectedRowIdsChanged(rowDataIds: string[]) {
        const selectedRow =
            rowDataIds.length === 1
                ? filteredData.find((r) => r.uuid === rowDataIds[0])
                : undefined;
        onRowSelected(selectedRow);
    }

    return (
        <div className="CorporateActionsWidgetGrid" style={style}>
            <Grid
                className="CorporateActionsGrid"
                columnDefs={columnDefs}
                rowData={filteredData || []}
                getRowDataId={(r) => r.uuid.toString()}
                suppressColumnVirtualisation={false}
                onGridReady={onGridReady}
                onSelectedRowIdsChanged={onSelectedRowIdsChanged}
                singleSelectOnly={true}
                getRowClass={getRowClass}
                {...gridPropsFromState}
                {...useWidgetEditedColumnNamesProps}
            />
        </div>
    );
}

function getRowClass({ data: rowData }: { data: EnrichedSymbolEvent }): string {
    const expr = new DateTimeExpression('Today');
    if (rowData.effectiveDate) {
        if (rowData.isTminus1AftMftEarnings) {
            return 'aftmkt-earnings-ontminus1';
        }
        if (expr.testSameDay(rowData.effectiveDate)) {
            return 'effective-date-today';
        }
        if (expr.testAfterThanDay(rowData.effectiveDate)) {
            return 'effective-date-upcoming';
        }
        return 'effective-date-historical';
    }

    return '';
}
